import {
    type MutationFunctionContext,
    type UseMutationOptions,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { api } from "api";
import { invalidateAfterTransaction } from "utils/useSharedTransactionInvalidation";
import type { TransactionRefundResult } from "api/responses";

type Options = UseMutationOptions<TransactionRefundResult, Error, number>;

/**
 * Client hook to refund a Transaction.
 * @param patientId The ID of the Patient/Responsible Party whose Transactions we are manipulating.
 * @param options Options for the client call; provides callbacks for when the API call succeeds or fails.
 * @returns React Query mutation properties.
 */
export function useTransactionRefund(patientId: number, options?: Options) {
    const queryClient = useQueryClient();

    /** Provide built-in onSuccess behavior, then call provided callback if available. */
    const onSuccess = (
        data: TransactionRefundResult,
        variables: number,
        onMutateResult: unknown,
        context: MutationFunctionContext,
    ) => {
        // Invalidate queries that are affected by refunding a transaction
        if (data.success || data.pending) {
            invalidateAfterTransaction(queryClient, "refund", patientId, variables);
        }

        // Use callback if provided
        return options?.onSuccess?.(data, variables, onMutateResult, context);
    };

    return useMutation({
        ...options,
        mutationFn: async (transactionId: number) => await api.transaction.refund(transactionId),
        onSuccess,
    });
}
